/**
 * high-striker-parts.js — greybox geometry for the High Striker tower.
 *
 * Pure builders, split out of `high-striker.js` so that module stays gameplay
 * only. Each returns a fresh Object3D; the stall supplies positions and the
 * physics descriptors, exactly as it does for anything out of
 * `assets3d/factory.js`. Nothing here reads or writes game state.
 *
 * These are stand-ins for the asset agent's eventual models. Keep the returned
 * shapes and their dimensions, and the stall's collider maths stays valid.
 *
 * @module stalls/high-striker-parts
 */

import * as THREE from 'three';
import { HIGH_STRIKER, PALETTE } from '../constants.js';
import { mat } from '../assets3d/factory.js';

const CFG = HIGH_STRIKER;

/**
 * The tower column the puck rides up.
 * @returns {THREE.Mesh}
 */
export function createColumn() {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(CFG.towerWidth, CFG.towerHeight, 0.22),
    mat(PALETTE.boothFrame, { rough: 0.85 }),
  );
  m.name = 'striker-column';
  m.castShadow = true;
  return m;
}

/**
 * One coloured zone plate.
 * @param {string} color CSS hex, from the zone table.
 * @returns {THREE.Mesh}
 */
export function createZonePlate(color) {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(CFG.towerWidth * 0.92, 0.055, 0.03),
    mat(new THREE.Color(color).getHex(), { rough: 0.5 }),
  );
  m.name = 'striker-zone';
  return m;
}

/**
 * The brass puck that gets launched up the tower.
 * @returns {THREE.Mesh}
 */
export function createPuck() {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(CFG.towerWidth * 0.72, 0.1, 0.09),
    mat(PALETTE.gold, { rough: 0.35, metal: 0.65 }),
  );
  m.name = 'striker-puck';
  m.castShadow = true;
  return m;
}

/**
 * The bell at the top. Built from a scaled sphere plus a skirt rather than a
 * partial-sphere geometry, so the shape stays legible at greybox scale.
 * @returns {THREE.Group}
 */
export function createBell() {
  const g = new THREE.Group();
  g.name = 'striker-bell';
  const brass = mat(PALETTE.gold, { rough: 0.25, metal: 0.85 });

  const dome = new THREE.Mesh(new THREE.SphereGeometry(0.13, 14, 10), brass);
  dome.scale.y = 0.78;
  g.add(dome);

  const skirt = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.14, 0.06, 14), brass);
  skirt.position.y = -0.06;
  g.add(skirt);

  const clapper = new THREE.Mesh(
    new THREE.SphereGeometry(0.035, 10, 8),
    mat(0x8a6a20, { rough: 0.4, metal: 0.6 }),
  );
  clapper.position.y = -0.11;
  g.add(clapper);

  return g;
}

/**
 * The lever pad the mallet lands on, with its supporting stand.
 * The group's origin is the centre of the pad plate.
 * @returns {THREE.Group}
 */
export function createLeverPad() {
  const g = new THREE.Group();
  g.name = 'striker-pad';

  const plate = new THREE.Mesh(
    new THREE.CylinderGeometry(CFG.padRadius, CFG.padRadius * 1.06, CFG.padThickness, 18),
    mat(0xc0392b, { rough: 0.6 }),
  );
  plate.castShadow = true;
  g.add(plate);

  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(CFG.padRadius, 0.022, 8, 22),
    mat(PALETTE.metal, { rough: 0.35, metal: 0.7 }),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = CFG.padThickness / 2;
  g.add(rim);

  const stand = new THREE.Mesh(
    new THREE.BoxGeometry(0.16, CFG.padY, 0.16),
    mat(PALETTE.boothFrame, { rough: 0.9 }),
  );
  stand.position.y = -CFG.padY / 2;
  g.add(stand);

  return g;
}

/**
 * The booth floor slab, so a dropped mallet lands on something.
 * @returns {THREE.Mesh}
 */
export function createFloorSlab() {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(3.6, 0.1, 3.6),
    mat(0x6a5a44, { rough: 1 }),
  );
  m.name = 'striker-floor';
  m.receiveShadow = true;
  return m;
}
